<template>
  <div
    ref="outerRef"
    class="desktop-frame-outer"
    :class="{ 'is-framed': showFrame }"
    :style="showFrame ? outerStyle : undefined"
  >
    <img
      v-if="showFrame"
      class="mockup-bg"
      :src="mockup.src"
      :style="imageStyle"
      alt=""
      aria-hidden="true"
    />
    <div ref="innerRef" class="desktop-frame-inner" :style="showFrame ? innerStyle : undefined">
      <div class="desktop-frame-content" :style="showFrame ? contentStyle : undefined">
        <RouterView v-if="!isLoading" />
      </div>
    </div>
  </div>
</template>

<script setup>
  import { useOverlayRect } from "@/composables/useOverlayRect";
  import { useBrandParameters } from "@/composables/useBrandParameters";
  import { useDesktopFrame } from "@/composables/useDesktopFrame";
  import { useDesktopMockup } from "@/composables/useDesktopMockup";
  import { usePageBackground } from "@/composables/usePageBackground";
  import { useApiStore } from "@/stores/api";
  import { useAppStore } from "@/stores/app";
  import { useElementSize } from "@vueuse/core";
  import { computed, onBeforeUnmount, onMounted, ref, watch, watchEffect } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { useTheme } from "vuetify";

  const store = useAppStore();
  const route = useRoute();
  const router = useRouter();
  const theme = useTheme();
  const isLoading = ref(true);
  const api = useApiStore();
  const { showFrame } = useDesktopFrame();
  const { backgroundColor: homeBackgroundColor, hasBackgroundImage: homeHasBackgroundImage } =
    usePageBackground();

  // Sorteado uma vez por acesso; fica fixo durante toda a navegação.
  const { mockup, naturalSize, screenRect } = useDesktopMockup();

  const outerRef = ref(null);
  const innerRef = ref(null);
  const { width: outerWidth, height: outerHeight } = useElementSize(outerRef);

  const SCROLLABLE_ROUTES = new Set([
    "ownershipWarning",
    "termsOfService",
    "journeysInProgress",
    "bikeModel",
    "selectBrand",
  ]);
  const isScrollableRoute = computed(() => SCROLLABLE_ROUTES.has(route.name));

  watch(
    () => route.name,
    () => {
      if (innerRef.value) {
        innerRef.value.scrollTop = 0;
      }
    },
  );

  const { rect, imageRect } = useOverlayRect(
    () => ({ width: outerWidth.value, height: outerHeight.value }),
    naturalSize,
    screenRect,
    "cover",
  );

  watchEffect(() => {
    document.documentElement.style.overflow = showFrame.value ? "hidden" : "";
    document.body.style.overflow = showFrame.value ? "hidden" : "";

    const root = document.documentElement;
    if (showFrame.value) {
      root.classList.add("desktop-frame-active");
      root.style.setProperty("--frame-real-left", `${rect.value.left}px`);
      root.style.setProperty("--frame-real-top", `${rect.value.top}px`);
      root.style.setProperty("--frame-real-width", `${rect.value.width}px`);
      root.style.setProperty("--frame-real-height", `${rect.value.height}px`);
      root.style.setProperty("--frame-content-scale", `${contentScale.value}`);
    } else {
      root.classList.remove("desktop-frame-active");
    }
  });

  onBeforeUnmount(() => {
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
    document.documentElement.classList.remove("desktop-frame-active");
  });

  // Posicionado explicitamente (não via `object-fit`) pra usar o mesmo
  // cálculo de `rect` — os dois nunca divergem.
  const imageStyle = computed(() => ({
    position: "absolute",
    left: `${imageRect.value.left}px`,
    top: `${imageRect.value.top}px`,
    width: `${imageRect.value.width}px`,
    height: `${imageRect.value.height}px`,
  }));

  // O conteúdo sempre faz layout como se estivesse numa tela de
  // REFERENCE_WIDTH (quebra de linha e tamanho de cada elemento calculados
  // nessa largura fixa) e só depois é escalado visualmente pro espaço real
  // que a foto deu (`rect.value.width`) — evita reflow estranho quando esse
  // espaço varia bastante entre janelas.
  //
  // `zoom`, não `transform: scale`: `transform` num ancestral cria um novo
  // containing block pra descendentes `position:fixed` (regra do CSS) — o
  // v-app-bar/v-footer de cada página, que vivem dentro do RouterView,
  // passariam a se posicionar relativos a este elemento em vez da janela
  // real. `zoom` não tem esse efeito colateral.
  const REFERENCE_WIDTH = 400;
  const contentScale = computed(() => rect.value.width / REFERENCE_WIDTH);

  const contentStyle = computed(() => ({
    width: `${REFERENCE_WIDTH}px`,
    // `vh` não respeita `zoom` (continua relativo à altura real da janela)
    // — sem este piso, uma página que depende só de `min-height:100vh`
    // pra preencher o fundo (a Home) fica mais baixa que a tela depois do
    // zoom, sobrando fundo padrão embaixo.
    minHeight: `${rect.value.height / contentScale.value}px`,
    zoom: contentScale.value,
    // `display:flex` pro filho direto (raiz do Vuetify) poder esticar até
    // esse `min-height` — percentual de altura não enxerga `min-height` de
    // ancestral, só `flex-grow` resolve sem travar numa altura fixa.
    display: "flex",
    flexDirection: "column",
  }));

  // Só o conteúdo usa `zoom` — o v-app-bar/v-footer ficam em largura real,
  // sem escala: o Vuetify mede a altura real do app-bar via JS pra calcular
  // o padding-top do v-main, e escalar os dois quebraria essa sincronia. O
  // conteúdo deles é curto (título, botão, ícone) e o Vuetify já trunca
  // sozinho numa tela estreita.

  const cornerRadius = computed(() => rect.value.width * mockup.cornerRadiusRatio);

  // homeBackgroundColor/homeHasBackgroundImage vêm de appTexts (boot único
  // em useBrandParameters) — cor fixa da Home. As demais páginas aplicam a
  // classe bg-background quando não há imagem de fundo; a Home não, por
  // isso ela é tratada à parte aqui.
  const isHomeRoute = computed(() =>
    ["home", "insurance", "homeWithTag", "storeHome"].includes(route.name),
  );

  const frameBackgroundColor = computed(() =>
    isHomeRoute.value || homeHasBackgroundImage.value
      ? homeBackgroundColor.value
      : theme.current.value?.colors?.background,
  );

  const outerStyle = computed(() => ({
    minHeight: "100vh",
    position: "relative",
    // flow-root evita que o margin-top de .desktop-frame-inner colapse com
    // o do outer (primeiro filho em fluxo normal).
    display: "flow-root",
    backgroundColor: frameBackgroundColor.value,
    transition: "background-color 0.3s ease",
    // v-app-bar/v-footer são descendentes de .desktop-frame-content, que
    // tem `zoom` aplicado — dentro de um elemento com zoom, left/top/width
    // de um descendente `position:fixed` também são multiplicados pela
    // escala do zoom, mesmo a ancoragem continuando na janela real. Por
    // isso os valores aqui são "de referência" (÷ contentScale): depois do
    // zoom multiplicar de volta, batem com a posição/tamanho reais.
    "--frame-width": `${REFERENCE_WIDTH}px`,
    "--frame-height": `${rect.value.height / contentScale.value}px`,
    "--frame-left": `${rect.value.left / contentScale.value}px`,
    "--frame-top": `${rect.value.top / contentScale.value}px`,
    "--frame-bottom": `${(outerHeight.value - rect.value.top - rect.value.height) / contentScale.value}px`,
    "--frame-radius": `${REFERENCE_WIDTH * mockup.cornerRadiusRatio}px`,
  }));

  // Altura travada (não min-height): cada página já define sua própria
  // min-height:100vh, que senão venceria o cálculo da tela do celular e
  // esticaria o conteúdo pra fora do aparelho na foto. Com altura travada +
  // overflow-y:auto, uma página mais alta que a tela rola por dentro dela.
  const innerStyle = computed(() => ({
    position: "relative",
    marginTop: `${rect.value.top}px`,
    marginLeft: `${rect.value.left}px`,
    width: `${rect.value.width}px`,
    height: `${rect.value.height}px`,
    borderRadius: `${cornerRadius.value}px`,
    overflowY: isScrollableRoute.value ? "auto" : "hidden",
    overflowX: "hidden",
    zIndex: 1,
  }));

  const setupTheme = async () => {
    const { defaultTheme: brandDefaultTheme } = await useBrandParameters();
    const currentTheme = theme.themes.value["defaultTheme"];

    theme.themes.value["defaultTheme"] = {
      ...currentTheme,
      dark: false,
      colors: brandDefaultTheme,
    };

    theme.change("defaultTheme");
  };

  async function setupInsuranceProgress() {
    const currentRoute = router.currentRoute.value;
    const insuranceCodeParam = currentRoute.params.insuranceCode;
    const insuranceCodeFromStorage = localStorage.getItem("insuranceCode");
    const isHomePath = currentRoute.path === "/";

    if (currentRoute.name === "storeHome") {
      return;
    }

    if (currentRoute.name === "home" && currentRoute.query.tag != null) {
      return;
    }

    if (currentRoute.name === "privacyPolicy") {
      return;
    }

    const insuranceCode = insuranceCodeParam || insuranceCodeFromStorage;

    if (!insuranceCode) {
      // Fluxo de tag: o cliente já foi resolvido (api.clientId, persistido em
      // sessionStorage), mas a Insurance ainda não existe (só nasce pós-auth).
      // Um refresh nesse meio-tempo não deve mandar o usuário de volta pra home.
      if (api.clientId) {
        return;
      }
      return router.replace({ path: "/" });
    }

    if (isHomePath && insuranceCodeFromStorage) {
      localStorage.removeItem("insuranceCode");
      return;
    }

    localStorage.setItem("insuranceCode", insuranceCode);
    store.setInsuranceCode(insuranceCode);
    api.insuranceCode = insuranceCode;

    await api.getData();
    const routeProgress = await store.retrieveInsuranceProgress();

    if (routeProgress) {
      router.replace({ path: String(routeProgress) });
    }
  }

  onMounted(async () => {
    await router.isReady();
    await store.loadBrandData(store.insuranceCode);
    await setupTheme();
    isLoading.value = false;

    await setupInsuranceProgress();
  });
</script>

<style lang="scss">
  // v-app-bar/v-footer usam position:fixed com left/right/top/bottom
  // relativos à janela, não ao container pai — por isso precisam das
  // custom properties --frame-* (setadas no .desktop-frame-outer, herdadas
  // por cascata mesmo sendo position:fixed).
  .desktop-frame-outer.is-framed {
    .v-app-bar,
    .v-footer {
      left: var(--frame-left) !important;
      right: auto !important;
      width: var(--frame-width) !important;
      transform: none !important;
    }

    // v-app-bar/v-footer não são filhos de .desktop-frame-inner (são
    // position:fixed, renderizados fora dele) — o overflow/border-radius
    // que arredonda o conteúdo não alcança os dois, por isso cada um
    // precisa do próprio raio (só o topo no app-bar, só a base no footer).
    .v-app-bar {
      top: var(--frame-top) !important;
      border-radius: var(--frame-radius) var(--frame-radius) 0 0 !important;
      overflow: hidden;
    }

    .v-footer {
      bottom: var(--frame-bottom) !important;
      border-radius: 0 0 var(--frame-radius) var(--frame-radius) !important;
      overflow: hidden;
    }

    .v-application.v-layout--full-height {
      height: var(--frame-height) !important;
    }

    .v-application__wrap {
      min-height: 0 !important;
    }

    .v-main {
      min-height: 0 !important;
    }
  }

  html.desktop-frame-active {
    .v-dialog.v-overlay,
    .v-dialog .v-overlay__scrim {
      left: var(--frame-real-left) !important;
      top: var(--frame-real-top) !important;
      right: auto !important;
      bottom: auto !important;
      width: var(--frame-real-width) !important;
      height: var(--frame-real-height) !important;
    }

    .v-dialog .v-overlay__content {
      zoom: var(--frame-content-scale, 1);
    }
  }

  .desktop-frame-outer {
    .desktop-frame-inner {
      // Barra de rolagem nativa some (rolagem em si continua funcionando)
      // — não existe em app de celular de verdade.
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }

      // Rede de segurança pra qualquer elemento pontual mais largo que o
      // esperado: quebra a palavra em vez de cortar.
      overflow-wrap: break-word;

      .desktop-frame-content > * {
        flex: 1 0 auto;
      }
    }

    .mockup-bg {
      z-index: 0;
      pointer-events: none;
    }
  }

  .debug {
    border: 1px solid red;
    background-color: rgba(255, 0, 0, 0.1);
  }

  .page-title {
    font-size: 2.25rem;
    font-weight: 900;
    font-family: Montserrat, sans-serif;
    line-height: 2.75rem;
    margin-bottom: 1.25rem;
  }

  p {
    margin-bottom: 1rem;
  }

  li {
    margin-left: 2rem;
  }

  .app-bg {
    background-size: cover;
    background-position: center;
  }

  .font-montserrat {
    font-family:
      Montserrat, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji",
      "Segoe UI Symbol", "Noto Color Emoji";
    font-optical-sizing: auto;
    font-weight: 400;
    font-style: normal;
  }

  .font-rubik {
    font-family:
      Rubik, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji",
      "Segoe UI Symbol", "Noto Color Emoji";
    font-optical-sizing: auto;
    font-weight: 400;
    font-style: normal;
  }

  .font-monospace {
    font-family:
      ui-monospace, "Cascadia Mono", Consolas, "Lucida Console", "Courier New", Courier, monospace;
    font-optical-sizing: auto;
    font-weight: 400;
    font-style: normal;
  }
</style>
